import { NotifyHelper } from 'classes/helpers/notify.helper';
import { VideoSelectionDialog } from 'components/common/dialogs/video-selection';
import { ErrorBoundary } from 'components/common/error-boundary';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { DEFAULT_DELIVERY, DeliveryType } from 'lib_ts/enums/pitches.enums';
import { ForUpdate } from 'lib_ts/interfaces/common/i-generics';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { first } from 'lodash';
import { useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import { useVideosStore } from 'stores/use-videos-store';

const COMPONENT_NAME = 'ChangeVideoDialog';

interface IProps {
  pitches: IPitch[];
  onClose: (result: IPitch[] | undefined) => void;
}

export const ChangeVideoDialog = (props: IProps) => {
  const deliveryDict = useMiscStore((s) => s.deliveryDict);

  const isSingle = props.pitches.length === 1;

  const firstPitch = first(props.pitches);

  const [delivery, setDelivery] = useState(
    (firstPitch ? deliveryDict[firstPitch._id] : undefined) ?? DEFAULT_DELIVERY
  );

  const defaultVideoID =
    isSingle && firstPitch
      ? (VideoHelper.getPitchDeliveryVideoID(firstPitch, delivery) ?? null)
      : null;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <VideoSelectionDialog
        identifier={COMPONENT_NAME}
        videoID={defaultVideoID}
        px={firstPitch?.bs.px ?? 0}
        onClose={() => props.onClose(undefined)}
        handleChange={async (videoID) => {
          // update the pitch only when video_id changes (even to undefined)
          const changed = props.pitches.filter(
            (p) => VideoHelper.getPitchDeliveryVideoID(p, delivery) !== videoID
          );

          if (changed.length === 0) {
            NotifyHelper.info({
              message_md: 'There are no changes to save.',
            });
            return;
          }

          // check if there are any validation issues with the selected video
          if (videoID) {
            const video = useVideosStore
              .getState()
              .videos.find((v) => v._id === videoID);

            if (!video) {
              NotifyHelper.error({
                message_md: `Video \`${videoID}\` does not exist in context. Please try again.`,
              });
              return;
            }
          }

          const updatedPitches = await usePitchListStore
            .getState()
            .updatePitches({
              payloads: changed.map((p) => {
                const o: ForUpdate<IPitch> = {
                  _id: p._id,
                };

                switch (delivery) {
                  case DeliveryType.Stretch: {
                    o.stretchVideoID = videoID || null;
                    break;
                  }

                  case DeliveryType.Windup:
                  default: {
                    o.windupVideoID = videoID || null;
                    break;
                  }
                }

                return o;
              }),
            });

          if (!updatedPitches) {
            return;
          }

          props.onClose(updatedPitches);
        }}
        delivery={delivery}
        onChangeDelivery={setDelivery}
        showDialog
      />
    </ErrorBoundary>
  );
};
